import { useMemo } from 'react';
import classNames from 'classnames';

/* Stores */
import { useResponsiveIsMobile } from '@store/client/store.responsive';
import { useDynamicPlanStore } from '@scenes/AddSkuPage/store/store.addsku';

/* Components */
import { isPartnerSubsidizedAddOn } from './utils';
import { SelectField, ELEMENT_SIZE, CalloutBox, CalloutBoxThemes } from 'ht-styleguide';
import { HelloTechNowMessage, WarningHintIcon } from '@components/UI/Messages';

/* Utils */
import { isPlainObjectWithKeys } from '@utils/object';

/* Styles */
import styles from './prequestioncheckbox.module.scss';
import { REMOTE_TRUE } from '@scenes/AddSkuPage/addsku.constants';

/* Types */
import { AdjAmountFormatted, Question, Sku } from 'types/sku.types';
import { IHash } from 'types/base.types';

type PreQuestionDropdownProps = {
  question: Question;
  sku: Sku;
  value: IHash<string>;
  error?: string;
  onChange: BaseAnyFunction;
};

const PreQuestionDropdown = (props: PreQuestionDropdownProps) => {
  /* Hooks */
  const isMobile = useResponsiveIsMobile();
  const { question, value, onChange, error, sku } = props;
  const isRemote = question.id === 'remote';

  const options = question.answers.map(a => {
    let label = a.text;
    const showAmount = a.adjAmountFormatted && !isPartnerSubsidizedAddOn({ answer: a, sku });

    if (a.id === REMOTE_TRUE) {
      const planDynamic = useDynamicPlanStore.getState().dynamicPlanSavings || sku.remoteSavingsFormatted;
      label = label.replace('%{SAVING}%', planDynamic);
    }
    if (showAmount) {
      // (a.adjAmountFormatted as AdjAmountFormatted)?.formatted ?? a.adjAmountFormatted;
      const answerAdjAmountFormatted = a.adjAmountFormatted;
      const amount = isPlainObjectWithKeys(answerAdjAmountFormatted)
        ? (answerAdjAmountFormatted as AdjAmountFormatted)?.formatted || '(Error)'
        : answerAdjAmountFormatted;

      label += ` (+${amount})`;
    }
    return { value: a.id, label };
  });

  /* Styles */
  const hintIconStyles = classNames(styles.answerHintIcon, styles.answerHintIconInput);

  const warning = useMemo(() => {
    const matchedSelectedAnswer = question.answers.find(a => a.id === value?.id);
    return matchedSelectedAnswer?.warning || '';
  }, [question.answers, value?.id]);

  return (
    <div className={styles.answer}>
      <div>
        <SelectField
          placeholder="Select"
          options={options}
          label={question.textDirect}
          value={value ? value.id : ''}
          onChange={({ value: v }) => onChange(v)}
          elementSize={isMobile ? ELEMENT_SIZE.SMALL : ELEMENT_SIZE.MEDIUM}
          clearable={false}
          searchable={false}
          error={error}
        />
        {isRemote && <HelloTechNowMessage />}
      </div>
      <WarningHintIcon content={question.hint} hintIconStyles={hintIconStyles} />
      {warning ? <CalloutBox className="marginY-small" theme={CalloutBoxThemes.DEFAULT} text={warning} /> : null}
    </div>
  );
};

export default PreQuestionDropdown;
